import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../../design/button";
import DialogWrapper from "../../../../../design/dialog-wrapper";
import Notice from "../../shift-details/notice";

export interface FailedAssignmentRow {
	key: string;
	shiftTimeLabel: string;
	jobRoleLabel: React.ReactNode;
	// Empty when the whole Shift's selection was rejected
	workerName: string;
	reason: string;
}

interface DirectAssignmentResultsProps {
	title: string;
	summary: string;
	failedRows: FailedAssignmentRow[];
	onDone: () => void;
}

const headerCellSx = {
	fontSize: "14px",
	fontWeight: 500,
	color: "rgba(0, 0, 0, 0.87)",
	borderBottomColor: "rgba(224, 224, 224, 1)",
	height: "56px",
};

const bodyCellSx = {
	fontSize: "14px",
	color: "#747474",
	borderBottomColor: "rgba(224, 224, 224, 1)",
	height: "52px",
};

// Shown after publishing or posting coverage when a picked Worker could not be
// assigned. Informational only: those spots stay open for signups.
const DirectAssignmentResults = ({
	title,
	summary,
	failedRows,
	onDone,
}: DirectAssignmentResultsProps) => {
	const { t } = useTranslation();

	return (
		<DialogWrapper
			dialogTitle={title}
			dialogSubTitle={summary}
			contentStyles={{ padding: "0px 45px" }}
			dialogContent={
				<Box>
					<Notice
						variant='normal'
						message={t("scheduler_not-assigned-notice", {
							count: failedRows.length,
						})}
					/>
					<Table sx={{ marginTop: "16px" }}>
						<TableHead>
							<TableRow>
								<TableCell sx={headerCellSx}>
									{t("scheduler_shift-time")}
								</TableCell>
								<TableCell sx={headerCellSx}>
									{t("scheduler_job-role-skills-required")}
								</TableCell>
								<TableCell sx={headerCellSx}>
									{t("scheduler_worker-name")}
								</TableCell>
								<TableCell sx={headerCellSx}>{t("scheduler_reason")}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{failedRows.map((row) => (
								<TableRow key={row.key}>
									<TableCell sx={bodyCellSx}>{row.shiftTimeLabel}</TableCell>
									<TableCell sx={bodyCellSx}>{row.jobRoleLabel}</TableCell>
									<TableCell
										sx={{
											...bodyCellSx,
											fontWeight: 500,
											color: "rgba(0, 0, 0, 0.87)",
										}}
									>
										{row.workerName || "—"}
									</TableCell>
									<TableCell sx={bodyCellSx}>
										<Box display='flex' alignItems='center' gap={1}>
											<InfoOutlinedIcon
												sx={{ color: "#E44B48", fontSize: "18px" }}
											/>
											<Typography sx={{ fontSize: "14px", color: "#747474" }}>
												{row.reason}
											</Typography>
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			}
			actionButtons={
				<Button color='primary' variant='contained' onClick={onDone}>
					{t("common_done")}
				</Button>
			}
		/>
	);
};

export default DirectAssignmentResults;
